import { createApiSlice } from '@/shared/features/query/config/query.build';
import type { HomePageDataDTO } from '../dto/homePageData.dto';
import { adaptHomePageDataDtoToDomain, homePageDataToPlainObject } from '../mappers/home.mapper';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import { HomeRepository } from "../../domain/interfaces/HomeRepository";
import { HomePageData } from "../../domain/models/HomePageData";
import type { AppDispatch } from "@/app/store/store";
import { executeQuery } from "@/shared/features/query/utils/queryUtils";

/**
 * Home Page API Slice
 * Creates query endpoints for home page feature
 */
const homePageApiSlice = createApiSlice({
  clientId: 'homePageApi',
  tagTypes: ['HomePage'],
  endpoints: {
    getHomePageData: {
      url: '/homePageData',
      method: 'POST',
      params: () => {
        const queryParams = {
          accessToken: 'Q7DDP0VqDmZb63G7Aesd',
          version: '',
          deviceType: 'm_jsp',
          _: Date.now(), // Timestamp for cache busting
        };
        return queryParams;
      },
      transformResponse: (response: unknown): Record<string, unknown> => {
        // The API returns stringified JSON, so we need to parse it
        let parsedResponse: unknown;
        
        if (typeof response === 'string') {
          try {
            parsedResponse = JSON.parse(response);
          } catch (error) {
            console.error('Error parsing home page API response:', error);
            parsedResponse = {};
          }
        } else {
          // If response is already an object, use it directly
          parsedResponse = response;
        }
        
        // DTO → Domain (applies domain logic) → Plain Object (for Redux)
        const domainModel = adaptHomePageDataDtoToDomain(parsedResponse as HomePageDataDTO);
        return homePageDataToPlainObject(domainModel);
      },
      keepUnusedDataFor: 300,
      providesTags: ['HomePage'],
    },
  },
});

export const homePageApi = homePageApiSlice.queryClient;
export const homePageApiRedux = homePageApiSlice.queryClient as ReduxQueryClient;

// Export hooks for home page (RTK Query-specific, but abstracted)
// These hooks are adapter-specific and may not exist for other adapters
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const hooks = homePageApiSlice.hooks as any;
export const useGetHomePageDataQuery = hooks?.useGetHomePageDataQuery;
export const useLazyGetHomePageDataQuery = hooks?.useLazyGetHomePageDataQuery;

/**
 * Home Repository Adapter
 * Implements HomeRepository using query client
 */
export class homeAdapter implements HomeRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async fetchHomePageData(): Promise<HomePageData> {
    try {
      const initiateResult = homePageApi.endpoints.getHomePageData.initiate({});
      // ExecuteQuery returns plain object from Redux, convert to domain model
      const plainObject = await executeQuery<Record<string, unknown>>(initiateResult, this.dispatch);
      return adaptHomePageDataDtoToDomain(plainObject);
    } catch (error) {
      console.error('Error fetching home page data:', error);
      return new HomePageData({});
    }
  }
}

